import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ArrowLeft, AlertTriangle, Clock, TrendingUp, ArrowRight, Info, Share2, Twitter, Facebook, Copy, Check } from "lucide-react";

// --- Helpers ---
const fmt = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);

const fmtShort = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`;
  return fmt(n);
};

function buildChartData(currentAge, retirementAge, monthly, annualRate, waitYears) {
  const monthlyRate = annualRate / 100 / 12;
  let totalA = 0;
  let totalB = 0;
  const data = [];

  for (let age = currentAge; age <= retirementAge; age++) {
    // Compound each year (12 months)
    for (let m = 0; m < 12; m++) {
      totalA = totalA * (1 + monthlyRate) + monthly;
      if (age >= currentAge + waitYears) {
        totalB = totalB * (1 + monthlyRate) + monthly;
      } else {
        totalB = 0; // flat / not investing yet
      }
    }
    data.push({
      age,
      startNow: Math.round(totalA),
      waitScenario: Math.round(totalB),
    });
  }
  return data;
}

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-slate-400 text-xs mb-2 font-medium">Age {label}</p>
        {payload.map((p) => (
          <div key={p.name} className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-slate-300">{p.name === "startNow" ? "Start Now" : "Wait"}:</span>
            <span className="font-semibold text-white">{fmtShort(p.value)}</span>
          </div>
        ))}
        {payload.length === 2 && (
          <div className="mt-2 pt-2 border-t border-slate-700 text-xs text-red-400 font-medium">
            Cost so far: {fmtShort(payload[0].value - payload[1].value)}
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default function CostOfWaiting() {
  const [currentAge, setCurrentAge] = useState(25);
  const [retirementAge, setRetirementAge] = useState(60);
  const [monthly, setMonthly] = useState(500);
  const [rate, setRate] = useState(8);
  const [waitYears, setWaitYears] = useState(5);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const yearsToRetire = retirementAge - currentAge;
  const yearsIfWait = retirementAge - (currentAge + waitYears);

  const chartData = useMemo(
    () => buildChartData(currentAge, retirementAge, monthly, rate, waitYears),
    [currentAge, retirementAge, monthly, rate, waitYears]
  );

  const finalA = chartData[chartData.length - 1]?.startNow || 0;
  const finalB = chartData[chartData.length - 1]?.waitScenario || 0;
  const costOfWaiting = finalA - finalB;
  const pctLost = finalA > 0 ? Math.round((costOfWaiting / finalA) * 100) : 0;

  // Share functionality
  const shareText = `I just discovered that waiting ${waitYears} year${waitYears !== 1 ? "s" : ""} to start investing could cost me ${fmt(costOfWaiting)} (${pctLost}% of my retirement pot)! Calculate your cost of waiting:`;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleShare = async (platform) => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    switch (platform) {
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, "_blank");
        break;
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, "_blank");
        break;
      case "copy":
        try {
          await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error("Failed to copy:", err);
        }
        break;
      case "native":
        if (navigator.share) {
          try {
            await navigator.share({
              title: "The Cost of Waiting",
              text: shareText,
              url: shareUrl,
            });
          } catch (err) {
            if (err.name !== "AbortError") {
              console.error("Share failed:", err);
            }
          }
        }
        break;
    }
    setShowShareMenu(false);
  };

  return (
    <div className="bg-slate-950 min-h-screen pt-16 pb-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Back */}
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          Back to Tools
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-red-400" />
          </div>
          <span className="text-red-400 text-sm font-medium">Wake-Up Call</span>
        </div>
        <h1
          data-testid="cost-of-waiting-title"
          className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight"
        >
          The Cost of Waiting
        </h1>
        <p className="text-slate-400 mb-10 max-w-2xl">
          See the real price of procrastination. Every year you delay investing has a compounding
          cost that grows far faster than you think.
        </p>

        {/* SHOCK BANNER */}
        <div
          data-testid="shock-banner"
          className="relative rounded-2xl border border-red-500/30 p-6 sm:p-8 mb-10 overflow-hidden"
          style={{
            background:
              "radial-gradient(ellipse 80% 100% at 50% 120%, rgba(239,68,68,0.07), transparent)",
          }}
        >
          <div className="absolute inset-0 bg-slate-900/60 rounded-2xl" />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm font-medium uppercase tracking-widest">
                  Cost of Waiting {waitYears} Year{waitYears !== 1 ? "s" : ""}
                </span>
              </div>
              <p
                data-testid="cost-of-waiting-stat"
                className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-red-400 tracking-tight"
              >
                -{fmt(costOfWaiting)}
              </p>
              <p className="text-slate-400 text-sm mt-2">
                That's <span className="text-red-400 font-semibold">{pctLost}%</span> of your
                potential retirement pot gone — forever.
              </p>
              
              {/* Share Button */}
              <div className="relative mt-4">
                <button
                  data-testid="share-shock-button"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                >
                  <Share2 className="w-4 h-4" />
                  Share this shock
                </button>
                
                {/* Share Menu Dropdown */}
                {showShareMenu && (
                  <div 
                    data-testid="share-menu"
                    className="absolute left-0 top-full mt-2 bg-slate-900 border border-slate-700 rounded-xl p-2 shadow-xl z-20 min-w-[180px]"
                  >
                    <button
                      onClick={() => handleShare("twitter")}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <Twitter className="w-4 h-4 text-sky-400" />
                      Share on X
                    </button>
                    <button
                      onClick={() => handleShare("facebook")}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <Facebook className="w-4 h-4 text-blue-400" />
                      Share on Facebook
                    </button>
                    <button
                      onClick={() => handleShare("copy")}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 text-slate-400" />
                          Copy link
                        </>
                      )}
                    </button>
                    {typeof navigator !== "undefined" && navigator.share && (
                      <button
                        onClick={() => handleShare("native")}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        <Share2 className="w-4 h-4 text-slate-400" />
                        More options...
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 flex-shrink-0">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center min-w-[130px]">
                <p className="text-slate-400 text-xs mb-1">Start Today</p>
                <p
                  data-testid="result-start-now"
                  className="font-outfit text-xl font-bold text-emerald-400"
                >
                  {fmtShort(finalA)}
                </p>
                <p className="text-slate-500 text-xs mt-0.5">{yearsToRetire} yrs investing</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center min-w-[130px]">
                <p className="text-slate-400 text-xs mb-1">Wait {waitYears} Yrs</p>
                <p
                  data-testid="result-wait"
                  className="font-outfit text-xl font-bold text-slate-400"
                >
                  {fmtShort(finalB)}
                </p>
                <p className="text-slate-500 text-xs mt-0.5">{yearsIfWait} yrs investing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Inputs */}
          <div
            data-testid="cost-inputs"
            className="lg:col-span-4 space-y-5"
          >
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="font-outfit font-semibold text-white text-lg">Your Scenario</h2>

              {/* Current Age */}
              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="text-slate-400 text-sm">Current Age</label>
                  <span className="text-white font-semibold text-sm">{currentAge}</span>
                </div>
                <input
                  type="range"
                  data-testid="input-current-age"
                  min="18"
                  max="55"
                  step="1"
                  value={currentAge}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setCurrentAge(v);
                    if (v >= retirementAge - 5) setRetirementAge(v + 5);
                  }}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>18</span><span>55</span>
                </div>
              </div>

              {/* Retirement Age */}
              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="text-slate-400 text-sm">Target Retirement Age</label>
                  <span className="text-white font-semibold text-sm">{retirementAge}</span>
                </div>
                <input
                  type="range"
                  data-testid="input-retirement-age"
                  min="40"
                  max="80"
                  step="1"
                  value={retirementAge}
                  onChange={(e) => setRetirementAge(Number(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>40</span><span>80</span>
                </div>
              </div>

              {/* Monthly Investment */}
              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="text-slate-400 text-sm">Monthly Investment</label>
                  <span className="text-white font-semibold text-sm">{fmt(monthly)}</span>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-monthly"
                    value={monthly}
                    onChange={(e) => setMonthly(Math.max(1, Number(e.target.value)))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Annual Return */}
              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="text-slate-400 text-sm">Annual Return Rate</label>
                  <span className="text-emerald-400 font-semibold text-sm">{rate}%</span>
                </div>
                <input
                  type="range"
                  data-testid="input-rate"
                  min="1"
                  max="15"
                  step="0.5"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>1%</span><span>15%</span>
                </div>
              </div>

              {/* Years to Wait — THE KEY SLIDER */}
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                <div className="flex justify-between mb-2">
                  <label className="text-red-300 text-sm font-medium flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Years You Wait to Start
                  </label>
                  <span className="text-red-400 font-bold text-sm">{waitYears} yr{waitYears !== 1 ? "s" : ""}</span>
                </div>
                <input
                  type="range"
                  data-testid="input-wait-years"
                  min="1"
                  max="10"
                  step="1"
                  value={waitYears}
                  onChange={(e) => setWaitYears(Number(e.target.value))}
                  className="w-full accent-red-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>1 yr</span><span>10 yrs</span>
                </div>
              </div>
            </div>

            {/* Key stats breakdown */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5 space-y-3">
              <p className="text-slate-400 text-sm font-medium">Breakdown</p>
              {[
                { label: "Total Contributed (Now)", value: fmt(monthly * 12 * yearsToRetire), cls: "text-slate-300" },
                { label: `Total Contributed (Wait ${waitYears}y)`, value: fmt(monthly * 12 * yearsIfWait), cls: "text-slate-400" },
                { label: "Growth Lost to Waiting", value: fmt(costOfWaiting - (monthly * 12 * waitYears)), cls: "text-red-400" },
              ].map((r) => (
                <div key={r.label} className="flex justify-between text-xs">
                  <span className="text-slate-500">{r.label}</span>
                  <span className={`font-mono font-medium ${r.cls}`}>{r.value}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-2 bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                Assumes monthly compounding at a fixed annual rate. Figures are illustrative only and do not account for inflation or charges. Not financial advice.
              </p>
            </div>
          </div>

          {/* Chart */}
          <div data-testid="cost-chart" className="lg:col-span-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-outfit font-semibold text-white text-lg">Growth Over Time</h2>
                  <p className="text-slate-500 text-sm">Projected portfolio value by age</p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    <span className="text-slate-400">Start Now</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1.5 rounded-full bg-slate-500 inline-block" />
                    <span className="text-slate-400">Wait {waitYears}yr{waitYears !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              </div>

              {/* SVG Gradient Defs */}
              <svg width="0" height="0" style={{ position: "absolute" }}>
                <defs>
                  <linearGradient id="gradA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="gradB" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#64748b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#64748b" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
              </svg>

              <ResponsiveContainer width="100%" height={360}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#1e293b"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="age"
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "#1e293b" }}
                    label={{ value: "Age", position: "insideBottom", offset: -2, fill: "#475569", fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={(v) => fmtShort(v)}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={60}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {/* Wait scenario behind */}
                  <Area
                    type="monotone"
                    dataKey="waitScenario"
                    name="waitScenario"
                    stroke="#64748b"
                    strokeWidth={2}
                    fill="url(#gradB)"
                    strokeDasharray="5 3"
                    dot={false}
                    activeDot={{ r: 4, fill: "#64748b", strokeWidth: 0 }}
                  />
                  {/* Start now on top */}
                  <Area
                    type="monotone"
                    dataKey="startNow"
                    name="startNow"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#gradA)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#10b981", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Gap annotation */}
              <div className="mt-4 flex items-center gap-3 border-t border-slate-800 pt-4">
                <div className="flex-1 bg-emerald-500/5 rounded-lg px-3 py-2 text-center">
                  <p className="text-slate-500 text-xs">At Retirement (Start Now)</p>
                  <p className="font-outfit font-bold text-emerald-400 text-lg">{fmtShort(finalA)}</p>
                </div>
                <div className="text-center px-2">
                  <p className="text-red-400 font-bold text-sm">VS</p>
                </div>
                <div className="flex-1 bg-slate-800/30 rounded-lg px-3 py-2 text-center">
                  <p className="text-slate-500 text-xs">At Retirement (Wait {waitYears}yr{waitYears !== 1 ? "s" : ""})</p>
                  <p className="font-outfit font-bold text-slate-400 text-lg">{fmtShort(finalB)}</p>
                </div>
              </div>
            </div>

            {/* Extra stat cards */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 text-center">
                <p className="text-slate-500 text-xs mb-1">Months of Lost Investing</p>
                <p className="font-outfit font-bold text-white text-xl">{waitYears * 12}</p>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 text-center">
                <p className="text-slate-500 text-xs mb-1">Cost Per Month Delayed</p>
                <p className="font-outfit font-bold text-red-400 text-xl">
                  {fmtShort(costOfWaiting / (waitYears * 12))}
                </p>
              </div>
              <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 text-center">
                <p className="text-slate-500 text-xs mb-1">% of Pot Lost</p>
                <p className="font-outfit font-bold text-red-400 text-xl">{pctLost}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky CTA */}
      <div
        data-testid="sticky-cta"
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-800/80 bg-slate-950/95 backdrop-blur-md px-4 py-4"
      >
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            <p className="text-slate-300 text-sm font-medium">
              Don't let procrastination cost you{" "}
              <span className="text-red-400 font-bold">{fmt(costOfWaiting)}</span>.
            </p>
            <p className="text-slate-500 text-xs">Start your ISA or SIPP today — every month counts.</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/tools"
              className="text-slate-400 hover:text-white text-sm transition-colors"
            >
              Explore all tools
            </Link>
            <a
              href="https://www.krishkara.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-guide-button"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm rounded-full px-6 py-2.5 transition-all duration-200 hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:-translate-y-0.5"
            >
              Get My Beginner's Guide
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
